import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CASE_STUDIES } from "../data/caseStudies";
import { setSeo } from "../lib/seo";

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cs = CASE_STUDIES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (cs) {
      setSeo({
        title: `${cs.name} — Case Study | vonmal`,
        description: cs.summary,
        image: cs.hero,
        url: `https://vonmal.com/work/${cs.slug}`,
      });
    }
  }, [slug, cs]);

  if (!cs) {
    return (
      <div className="bg-void min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center">
          <h1 className="font-heading text-3xl font-black">Case study not found</h1>
          <button onClick={() => navigate("/")} className="mt-6 text-neon-cyan hover:underline">← Back home</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-void min-h-screen">
      <Navbar />
      <article className="pt-28 pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            state={{ scrollTo: "work" }}
            data-testid="casestudy-back"
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-neon-cyan transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Back to work
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-neon-cyan mb-3">{cs.tag}</p>
            <h1 data-testid="casestudy-title" className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              {cs.name}
            </h1>
            <p className="mt-4 text-lg text-white/60 max-w-2xl leading-relaxed">{cs.summary}</p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-2 font-mono text-sm text-neon-yellow border border-neon-yellow/30 rounded-full px-4 py-1.5">
                <Clock size={14} /> Built in {cs.timeline}
              </span>
              {cs.url && (
                <a
                  href={cs.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="casestudy-live-link"
                  className="inline-flex items-center gap-1 text-sm text-neon-cyan hover:underline"
                >
                  Visit live site <ArrowUpRight size={15} />
                </a>
              )}
            </div>
          </motion.div>

          <div className="mt-10 rounded-2xl overflow-hidden border border-neon-cyan/15">
            <img src={cs.hero} alt={cs.name} className="w-full h-[320px] sm:h-[420px] object-cover" />
          </div>

          <div className="mt-12 grid gap-10 md:grid-cols-2">
            <div>
              <h2 className="font-heading text-xl font-bold mb-3 text-neon-cyan">The challenge</h2>
              <p className="text-white/65 leading-relaxed">{cs.challenge}</p>
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold mb-3 text-neon-cyan">What we built</h2>
              <p className="text-white/65 leading-relaxed">{cs.solution}</p>
            </div>
          </div>

          <div className="mt-12 grid gap-10 md:grid-cols-2">
            <div>
              <h2 className="font-heading text-xl font-bold mb-4">Outcomes</h2>
              <ul className="space-y-3">
                {cs.results.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70">
                    <Check size={18} className="text-neon-cyan mt-0.5 shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold mb-4">Stack</h2>
              <div className="flex flex-wrap gap-2">
                {cs.stack.map((s, i) => (
                  <span key={i} className="font-mono text-xs text-white/60 border border-white/10 rounded-full px-3 py-1.5">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-16 rounded-2xl border border-neon-cyan/20 bg-surface p-8 text-center">
            <h3 className="font-heading text-2xl font-black tracking-tight">Want something like this?</h3>
            <p className="mt-2 text-white/55">Get a live price &amp; timeline in under a minute.</p>
            <Link
              to="/"
              state={{ scrollTo: "estimate" }}
              data-testid="casestudy-cta"
              className="neon-btn inline-block mt-6 bg-neon-cyan text-black font-semibold px-7 py-3 rounded-full"
            >
              Build your app
            </Link>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
